/**
 * AttributeChipInput — chips + curated suggestions + free-form entry.
 * Used by the product form's Attributes section (UI/UX pass PR 2).
 */
import { useState } from 'react'
import { X, Plus } from 'lucide-react'

export interface AttributeChipInputProps {
  label: string
  inputId: string
  values: string[]
  onChange: (values: string[]) => void
  suggestions?: readonly string[]
  placeholder?: string
}

export function AttributeChipInput({
  label, inputId, values, onChange, suggestions = [], placeholder,
}: AttributeChipInputProps) {
  const [draft, setDraft] = useState('')

  const add = (raw: string) => {
    const value = raw.trim()
    if (!value || values.includes(value)) return
    onChange([...values, value])
  }
  const remove = (value: string) => onChange(values.filter(v => v !== value))
  const remaining = suggestions.filter(s => !values.includes(s))

  return (
    <div>
      <label htmlFor={inputId} className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {values.map(v => (
            <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
              {v}
              <button type="button" aria-label={`Remove ${v}`} onClick={() => remove(v)}
                className="hover:text-primary/70">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        id={inputId}
        value={draft}
        placeholder={placeholder ?? 'Type and press Enter'}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            add(draft)
            setDraft('')
          }
        }}
        className="w-full px-3 py-1.5 text-sm rounded-sm border border-border bg-background"
      />
      {remaining.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {remaining.map(s => (
            <button key={s} type="button" aria-label={`Add ${s}`} onClick={() => add(s)}
              className="inline-flex items-center gap-0.5 px-2 py-0.5 text-xs rounded-full border border-border text-muted-foreground hover:border-primary/40 hover:text-primary">
              <Plus className="w-3 h-3" />{s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
